import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { getDashboard, type BadgeItem, type SavedItem, type HistoryItem, type ProfileRow, type UserCuriosityRow } from "@/lib/account.functions";
import { getMyGameDashboard, type SavedQuizRow, type SavedCollectionRow } from "@/lib/game.functions";
import { getMyProfile } from "@/lib/profile.functions";
import { ProfileEditor } from "@/components/ProfileEditor";
import { ArchetypeQuiz } from "@/components/ArchetypeQuiz";
import { ARCHETYPES } from "@/lib/archetypes";
import { Flame, BookMarked, Brain, Trophy, Sparkles, Award, ArrowRight, Lock, Gamepad2, FolderHeart, Bookmark, UserCircle, Wand2, Download as DownloadIcon, ShieldAlert } from "lucide-react";
import { exportMyData, deleteMyAccount } from "@/lib/gdpr.functions";
import { supabase } from "@/integrations/supabase/client";
import { CuriosityJourney } from "@/components/CuriosityJourney";

export const Route = createFileRoute("/_authenticated/me")({
  head: () => ({ meta: [{ title: "My Dashboard - Smartasaurus" }, { name: "robots", content: "noindex" }] }),
  component: MePage,
});

type DashboardData = {
  profile: (ProfileRow & { username?: string | null }) | null;
  curiosity: UserCuriosityRow | null;
  saved: SavedItem[];
  history: HistoryItem[];
  badges: BadgeItem[];
};

type GameDash = Awaited<ReturnType<typeof getMyGameDashboard>>;
type FullProfile = Awaited<ReturnType<typeof getMyProfile>>;

function MePage() {
  const fetch = useServerFn(getDashboard);
  const fetchGame = useServerFn(getMyGameDashboard);
  const fetchProfile = useServerFn(getMyProfile);
  const [data, setData] = useState<DashboardData | null>(null);
  const [game, setGame] = useState<GameDash | null>(null);
  const [fullProfile, setFullProfile] = useState<FullProfile | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function reloadProfile() {
    fetchProfile().then((p) => setFullProfile(p));
  }

  useEffect(() => {
    fetch().then((d) => setData(d as DashboardData)).catch((e) => setErr(e instanceof Error ? e.message : "Failed to load"));
    fetchGame().then((g) => setGame(g)).catch(() => {});
    fetchProfile().then((p) => setFullProfile(p)).catch(() => {});
  }, [fetch, fetchGame, fetchProfile]);


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-24">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Your Dashboard</div>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] md:text-7xl">
          {data?.profile?.display_name || data?.profile?.username ? <>Welcome back, <span className="text-gradient-curiosity">{data.profile.display_name || data.profile.username}</span>.</> : "Your discovery hub."}
        </h1>

        {err && <p className="mt-6 text-sm text-destructive">{err}</p>}
        {!data && !err && <p className="mt-6 text-sm text-muted-foreground">Loading…</p>}

        {data && (
          <>
            <section className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat icon={<Sparkles className="h-4 w-4" />} label="Curiosity Score" value={data.curiosity?.score ?? 0} />
              <Stat icon={<Flame className="h-4 w-4" />} label="Streak" value={`${data.curiosity?.streak_days ?? 0}d`} />
              <Stat icon={<Brain className="h-4 w-4" />} label="Discoveries Read" value={data.curiosity?.discoveries_viewed ?? 0} />
              <Stat icon={<Trophy className="h-4 w-4" />} label="Quizzes Won" value={data.curiosity?.quizzes_completed ?? 0} />
            </section>

            <CuriosityJourney
              curiosity={data.curiosity}
              perCat={(game?.gameState as any)?.perCat ?? {}}
              badges={data.badges}
            />


            {/* Profile + archetype */}
            <section id="profile" className="mt-10 scroll-mt-24">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                    <UserCircle className="h-3.5 w-3.5" /> Your Smartasaurus
                  </div>
                  <h2 className="mt-2 font-display text-3xl uppercase">
                    {(() => {
                      const a = fullProfile?.archetype ? ARCHETYPES[fullProfile.archetype as keyof typeof ARCHETYPES] : null;
                      return a ? <>You're <span className="text-gradient-curiosity">{a.name}</span> {a.emoji}</> : "Find your type";
                    })()}
                  </h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {fullProfile?.username && (
                    <>
                      <Link to="/u/$username" params={{ username: fullProfile.username }} className="rounded-full border border-border px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-primary">
                        View public profile
                      </Link>
                      <button
                        onClick={async () => {
                          const url = `${window.location.origin}/u/${fullProfile.username}`;
                          if (navigator.share) {
                            try { await navigator.share({ title: 'My Smartasaurus profile', url }); return; } catch {}
                          }
                          try { await navigator.clipboard.writeText(url); alert('Profile link copied!'); } catch { window.prompt('Copy your profile link:', url); }
                        }}
                        className="rounded-full border border-border px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-primary"
                      >
                        Share profile
                      </button>
                    </>
                  )}
                  <button onClick={() => setShowQuiz((v) => !v)} className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-primary hover:bg-primary/10">
                    <Wand2 className="h-3.5 w-3.5" /> {fullProfile?.archetype ? "Retake quiz" : "Take quiz"}
                  </button>
                  <button onClick={() => setShowEditor((v) => !v)} className="rounded-full bg-primary px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-primary-foreground hover:brightness-110">
                    {showEditor ? "Close" : "Edit profile"}
                  </button>
                </div>
              </div>

              {showQuiz && (
                <div className="mt-5">
                  <ArchetypeQuiz
                    initialSeed={fullProfile?.avatar_seed ?? null}
                    initialStyle={fullProfile?.avatar_style ?? null}
                    onDone={() => { setShowQuiz(false); reloadProfile(); }}
                  />
                </div>
              )}

              {showEditor && (
                <div className="mt-5">
                  <ProfileEditor initial={{
                    username: fullProfile?.username ?? null,
                    display_name: fullProfile?.display_name ?? null,
                    bio: fullProfile?.bio ?? null,
                    interests: fullProfile?.interests ?? null,
                    occupation: fullProfile?.occupation ?? null,
                    country: fullProfile?.country ?? null,
                    archetype: fullProfile?.archetype ?? null,
                    avatar_seed: fullProfile?.avatar_seed ?? null,
                    avatar_style: fullProfile?.avatar_style ?? null,
                    is_public: fullProfile?.is_public ?? true,
                  }} />
                </div>
              )}
            </section>


            <DiscoverySection title="Continue Reading" href="/history" emptyHref="/discoveries" emptyLabel="Browse discoveries" items={data.history} kind="history" />
            <DiscoverySection title="Saved" href="/saved" emptyHref="/discoveries" emptyLabel="Find something to save" items={data.saved} kind="saved" />

            <section className="mt-14 border-t border-border pt-10">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
                <Award className="h-3.5 w-3.5" /> Badges
              </div>
              <h2 className="mt-2 font-display text-3xl uppercase">Your achievements</h2>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                {data.badges.map((b) => (
                  <div key={b.slug} className={`rounded-2xl border p-4 ${b.earned_at ? "border-primary/40 bg-primary/5" : "border-border bg-card/40 opacity-60"}`}>
                    <div className="flex items-center justify-between">
                      <Award className="h-5 w-5 text-primary" />
                      {!b.earned_at && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                    </div>
                    <div className="mt-3 font-display text-base uppercase">{b.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{b.description}</div>
                  </div>
                ))}
              </div>
            </section>

            <GameSection game={game} />
            <SavedQuizSection items={game?.savedQuizzes ?? []} />
            <SavedCollectionSection items={game?.savedCollections ?? []} />
            <PrivacySection />
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">{icon}{label}</div>
      <div className="mt-2 font-display text-3xl md:text-4xl">{value}</div>
    </div>
  );
}

function DiscoverySection({ title, href, emptyHref, emptyLabel, items, kind }: {
  title: string; href: "/saved" | "/history"; emptyHref: "/discoveries";
  emptyLabel: string; items: (SavedItem | HistoryItem)[]; kind: "saved" | "history";
}) {
  return (
    <section className="mt-14 border-t border-border pt-10">
              <div className="mb-5 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
        <h2 className="font-display text-3xl uppercase">{title}</h2>
                <Link to={href} className="shrink-0 text-xs font-bold uppercase tracking-wider text-primary">View all →</Link>
      </div>
      {items.length === 0 ? (
        <Link to={emptyHref} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:border-primary">
          <BookMarked className="h-3.5 w-3.5" /> {emptyLabel} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          {items.slice(0, 4).map((it) => (
            <Link key={it.discovery_id} to="/discovery/$slug" params={{ slug: it.slug }} className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:border-primary/40">
              {it.image_url && <img src={it.image_url} alt={it.title} loading="lazy" className="aspect-[5/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
              <div className="p-3 md:p-4">
                {it.category_label && <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{it.category_label}</div>}
                <div className="mt-1 line-clamp-2 font-display text-sm uppercase leading-tight md:text-lg">{it.title}</div>
                {kind === "history" && (
                  <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-border">
                    <div className="h-full bg-gradient-curiosity" style={{ width: `${(it as HistoryItem).progress}%` }} />
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function GameSection({ game }: { game: GameDash | null }) {
  const s: any = game?.gameState ?? {};
  const totalPlays = s.totalPlays ?? 0;
  const bestScore = s.bestScore ?? 0;
  const bestStreak = s.bestStreak ?? 0;
  const totalCorrect = s.totalCorrect ?? 0;
  const achievements = (s.achievements ?? []).length;
  const per = s.perCat ?? {};
  const cats: { key: string; label: string }[] = [
    { key: "animals", label: "Animals" }, { key: "space", label: "Space" },
    { key: "body", label: "Human Body" }, { key: "weird", label: "Weird Science" },
  ];

  return (
    <section className="mt-14 border-t border-border pt-10">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <Gamepad2 className="h-3.5 w-3.5" /> Game Progress
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <h2 className="font-display text-3xl uppercase">Your stats</h2>
        <Link to="/play" className="rounded-full bg-primary px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary-foreground hover:brightness-110">
          Play <ArrowRight className="ml-1 inline h-3 w-3" />
        </Link>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat icon={<Gamepad2 className="h-4 w-4" />} label="Rounds played" value={totalPlays} />
        <Stat icon={<Trophy className="h-4 w-4" />} label="Best score" value={bestScore} />
        <Stat icon={<Flame className="h-4 w-4" />} label="Best streak" value={bestStreak} />
        <Stat icon={<Brain className="h-4 w-4" />} label="Total correct" value={totalCorrect} />
        <Stat icon={<Award className="h-4 w-4" />} label="Achievements" value={achievements} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {cats.map((c) => {
          const pc = per[c.key] ?? { correct: 0, plays: 0, bestScore: 0 };
          const mastery = Math.min(100, Math.round((pc.correct / 25) * 100));
          return (
            <div key={c.key} className="rounded-2xl border border-border bg-card p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{c.label}</div>
              <div className="mt-2 font-display text-2xl">{mastery}%</div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${mastery}%` }} />
              </div>
              <div className="mt-2 text-[10px] text-muted-foreground">{pc.correct} correct · {pc.plays} plays</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SavedQuizSection({ items }: { items: SavedQuizRow[] }) {
  return (
    <section className="mt-14 border-t border-border pt-10">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <Bookmark className="h-3.5 w-3.5" /> Saved Quizzes
      </div>
      <h2 className="mt-2 font-display text-3xl uppercase">Quizzes you bookmarked</h2>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No saved quizzes yet. <Link to="/play" className="text-primary underline">Play a round →</Link>
        </p>
      ) : (
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          {items.map((q) => (
            <Link key={q.id} to="/play" search={{ quiz: q.slug }} className="rounded-2xl border border-border bg-card p-4 transition hover:border-primary">
              <div className="line-clamp-2 text-sm font-semibold">{q.question}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function SavedCollectionSection({ items }: { items: SavedCollectionRow[] }) {
  return (
    <section className="mt-14 border-t border-border pt-10">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <FolderHeart className="h-3.5 w-3.5" /> Saved series
      </div>
      <h2 className="mt-2 font-display text-3xl uppercase">Series you bookmarked</h2>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No saved series yet. <Link to="/collections" className="text-primary underline">Browse series →</Link>
        </p>
      ) : (
        <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {items.map((c) => (
            <Link key={c.id} to="/collections/$slug" params={{ slug: c.slug }} className="rounded-2xl border border-border bg-card p-4 transition hover:border-primary">
              <div className="font-display text-base uppercase">{c.title}</div>
              <div className="mt-1 text-xs text-muted-foreground">/{c.slug}</div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

function PrivacySection() {
  const doExport = useServerFn(exportMyData);
  const doDelete = useServerFn(deleteMyAccount);
  async function handleExport() {
    const data = await doExport();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `smartasaurus-data-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
  async function handleDelete() {
    const first = window.prompt('Type DELETE to permanently erase your account. This cannot be undone.');
    if (first !== 'DELETE') return;
    try {
      await doDelete();
      await supabase.auth.signOut();
      window.location.href = "/";
    } catch (e) {
      alert(e instanceof Error ? e.message : "Could not delete account.");
    }
  }
  return (
    <section className="mt-14 border-t border-border pt-10">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        <ShieldAlert className="h-3.5 w-3.5" /> Privacy & Data
      </div>
      <h2 className="mt-2 font-display text-3xl uppercase">Your data, your choice</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Download a copy of everything we store about you, or permanently delete your account. Deletion removes your profile, saved items, history, comments and any other data tied to your account.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <button
          onClick={handleExport}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[11px] font-bold uppercase tracking-widest hover:border-primary"
        >
          <DownloadIcon className="h-3.5 w-3.5" /> Export my data
        </button>
        <button
          onClick={handleDelete}
          className="inline-flex items-center gap-2 rounded-full border border-destructive/40 bg-destructive/10 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-destructive hover:bg-destructive/20"
        >
          <ShieldAlert className="h-3.5 w-3.5" /> Delete my account
        </button>
      </div>
    </section>
  );
}
